import React, { useCallback, useEffect, useState } from "react";
import { TailSpin } from "react-loader-spinner";

import { useAuth } from "../../context/auth-context";
import {
  getPushNotificationState,
  subscribeAdminToPush,
  unsubscribeAdminFromPush,
  type PushNotificationState,
} from "../../utils/pushNotifications";

const Security = () => {
  const { isAuthenticated } = useAuth();
  const [pushState, setPushState] = useState<PushNotificationState>({
    status: "checking",
    message: "Checking push notification status...",
  });
  const [pushBusy, setPushBusy] = useState(false);

  const refreshPushState = useCallback(async () => {
    try {
      const state = await getPushNotificationState();
      setPushState(state);
    } catch (error) {
      setPushState({
        status: "disabled",
        message:
          error instanceof Error
            ? error.message
            : "Could not check push notification status.",
      });
    }
  }, []);

  useEffect(() => {
    void refreshPushState();
  }, [refreshPushState]);

  const pushEnabled = pushState.status === "enabled";
  const pushUnavailable =
    pushState.status === "checking" ||
    pushState.status === "unsupported" ||
    pushState.status === "missing-key" ||
    pushState.status === "denied";

  const togglePushNotifications = async () => {
    if (pushBusy || pushUnavailable || !isAuthenticated) return;

    setPushBusy(true);
    try {
      if (pushEnabled) {
        await unsubscribeAdminFromPush();
      } else {
        await subscribeAdminToPush();
      }
      await refreshPushState();
    } catch (error) {
      setPushState({
        status: pushEnabled ? "enabled" : "disabled",
        message:
          error instanceof Error
            ? error.message
            : "Could not update push notification settings.",
      });
    } finally {
      setPushBusy(false);
    }
  };

  const pushButtonLabel = pushEnabled
    ? "Disable notifications"
    : "Enable notifications";

  return (
    <div className="px-8 mds:px-12 lgss:px-20 pb-16 pt-16">
      <div className="flex justify-start gap-16 mds:gap-24 lgss:gap-44 items-center">
        <div className="">
          <h4 className="text-[16px] font-semibold text-white">
            Reset password
          </h4>
          <h4 className="text-[13px] text-[#F7F8F9] pt-2">
            Considering changing your password?
            <br />
            Tap to reset password
          </h4>
        </div>
        <button
          // onClick={resetPassword}
          className="flex font-bold justify-center  whitespace-nowrap items-center text-secondary_button"
        >
          {/* {loading ? (
        <div className="flex justify-center items-center">
          <TailSpin color="#0B75C2" height={30} width={30} />
        </div>
      ) : (
        "Reset Password"
      )} */}{" "}
          Reset Password
        </button>
      </div>
      <div className="flex flex-col justify-start gap-6 mt-16 lgss:flex-row lgss:items-center lgss:gap-28">
        <div className="max-w-[420px]">
          <h4 className="text-[16px] font-semibold text-white">
            Push notifications
          </h4>
          <h4 className="text-[13px] text-[#F7F8F9] pt-2">
            Receive security and admin alerts on this device.
            <br />
            You can disable them from this browser at any time.
          </h4>
          <p
            className={`mt-3 text-[12px] ${
              pushState.status === "enabled"
                ? "text-[#03C668]"
                : pushState.status === "denied"
                ? "text-[#D92B2B]"
                : "text-[#869AA9]"
            }`}
          >
            {pushState.message}
          </p>
        </div>
        <button
          type="button"
          onClick={togglePushNotifications}
          disabled={pushBusy || pushUnavailable}
          className={`flex h-[54px] w-[220px] items-center justify-center rounded-lg font-bold transition lgss:h-[48px] ${
            pushEnabled
              ? "bg-[#FDE8E8] text-[#D92B2B]"
              : "bg-secondary_button text-white"
          } disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {pushBusy ? (
            <TailSpin color="#ffffff" height={22} width={22} />
          ) : (
            pushButtonLabel
          )}
        </button>
      </div>
      <div className="flex justify-start gap-8 lgss:gap-28 mt-16 items-center">
        <div className="">
          <h4 className="text-[16px] font-semibold text-white">
            Delete account
          </h4>
          <h4 className="text-[13px] text-[#F7F8F9] pt-2">
            This is really sad and we don’t want to lose you,
            <br />
            but if you really must, tap to delete.
          </h4>
        </div>
        <button className="flex w-[180px] font-bold justify-center items-center text-[#D92B2B] bg-[#FDE8E8] h-[54px] lgss:h-[48px] rounded-lg">
          Deactivate account
        </button>
      </div>
    </div>
  );
};

export default Security;
